import React, { useCallback, useEffect, useRef, useState } from 'react';
import compose from 'recompose/compose';
import { connect } from 'react-redux';
import { ApplicationState } from '../../../store';
import Badge from '@mui/material/Badge';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import Checkbox from '@mui/material/Checkbox';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { SelectedDebtorClaim, FeeType, LeapClaim, PaymentType } from '../../../api/client';
import { formatDate } from '../../../components/Utils';
import { colors } from '../../../colors';
import { getConversationActions, isDebtorClaimSelectableForConversationSupervisorStep } from '../../../store/Selectors';
import { actionCreators as DebtorClaimsStoreActionCreators } from '../../../store/DebtorClaimsStore';
import { actionCreators as DebtorLeapClaimsStoreActionCreators } from '../../../store/DebtorLeapClaimsStore';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DebtorClaimIcon from '../../debtorOverview/presentation/DebtorClaimIcon';
import DebtorClaimSystemBadge from '../../debtorOverview/presentation/DebtorClaimSystemBadge';
import DebtorPaymentInformation from '../../debtorOverview/presentation/DebtorPaymentInformation';
import DebtorClaimEventComponent from '../../debtorClaimEvents/presentation/DebtorClaimEvent';
import DebtorClaimChildren from '../../debtorOverview/presentation/DebtorClaimChildren';
import ExternalLinkToInvoice from '../../debtorOverview/presentation/ExternalLinkToInvoice';
import ExternalDebtCollectionInfo from '../presentation/ExternalDebtCollectionInfo';
import CreditorName from '../../debtorOverview/presentation/CreditorName';
import ClaimPaymentInfoCopyButton from '../presentation/ClaimPaymentInfoCopyButton';
import ClaimInformationIcon from '../presentation/ClaimInformationIcon';
import currencyFormatter from 'currency-formatter';
import { useAppDispatch } from '../../../hooks';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';

interface PropsFromState {
  expanded?: boolean;
  showCheckBox: boolean;
  isSelected: boolean;
  conversationActions: number;
  isSelectable: boolean;
}

interface InjectedProps {
  debtorClaim: LeapClaim;
  isTopLevel: boolean;
}

type ComponentProps = PropsFromState & InjectedProps;

const DebtorClaimComponent = (props: ComponentProps) => {
  const {
    expanded,
    isTopLevel,
    debtorClaim,
    conversationActions,
    showCheckBox,
    isSelectable,
    isSelected,
  } = props;

  const [isExpanded, setIsExpanded] = useState(false);
  const hasAutoExpanded = useRef(false);

  const bannerBackgroundColor = () => {
    if (props.debtorClaim.transferredToExternalCollection) {
      return colors.secondary.lemon + '80';
    }

    if (!isExpanded) {
      return colors.white;
    } else {
      return colors.lightCyan;
    }
  }

  const collapsePanelBackgroundColor = isTopLevel ? colors.white : colors.lightCyan;

  const dispatch = useAppDispatch();

  const toggleDebtorClaimSelection = useCallback((debtorClaim: SelectedDebtorClaim) => {
    dispatch(DebtorClaimsStoreActionCreators.toggleDebtorClaimSelection(debtorClaim));
  }, [dispatch]);

  const clearDebtorClaimsSelection = useCallback(() => {
    dispatch(DebtorClaimsStoreActionCreators.clearDebtorClaimsSelection());
  }, [dispatch]);

  const getLeapDebtCollectionEvents = useCallback((caseNumber: string) => {
    dispatch(DebtorLeapClaimsStoreActionCreators.getLeapDebtCollectionEvents(caseNumber));
  }, [dispatch]);

  const getLeapEvents = useCallback((claimId: string) => {
    dispatch(DebtorLeapClaimsStoreActionCreators.getLeapEvents(claimId));
  }, [dispatch]);

  useEffect(() => {
    clearDebtorClaimsSelection();
  }, [clearDebtorClaimsSelection]);

  const handleExpandClick = useCallback(() => {
    setIsExpanded(!isExpanded);
    if (!debtorClaim.eventsHaveBeenFetched) {
      if (debtorClaim.type === 'LeapClaim') {
        getLeapEvents(debtorClaim.externalReferenceId!.toString());
      } else if (debtorClaim.type === 'LeapDebtCollectionClaim') {
        getLeapDebtCollectionEvents(debtorClaim.number!.toString());
      }
    }
  }, [debtorClaim.eventsHaveBeenFetched, debtorClaim.externalReferenceId, debtorClaim.number, debtorClaim.type, getLeapDebtCollectionEvents, getLeapEvents, isExpanded]);

  useEffect(() => {
    if (expanded && !hasAutoExpanded.current) {
      hasAutoExpanded.current = true;
      handleExpandClick();
    }
  }, [expanded, handleExpandClick]);

  const handleCheckboxChange = useCallback(() => {
    toggleDebtorClaimSelection({
      providerSystem: debtorClaim.providerSystem,
      number: debtorClaim.number!.toString(),
      type: debtorClaim.type!.toString(),
      externalReferenceId: debtorClaim.externalReferenceId!,
      balanceAmount: debtorClaim.amount,
      invoiceFeeAmount: debtorClaim.fees?.filter(x => x.type === FeeType.InvoiceFee).reduce((sum, current) => sum + current.amount, 0),
      noticeFeeAmount: debtorClaim.fees?.filter(x => x.type === FeeType.DebtCollectionNoticeFee).reduce((sum, current) => sum + current.amount, 0),
    });
  }, [debtorClaim.providerSystem, debtorClaim.externalReferenceId, debtorClaim.number, debtorClaim.type, toggleDebtorClaimSelection, debtorClaim.amount, debtorClaim.fees]);

  const enabledOpacity = 1;
  const disabledOpacity = 0.3;

  const getOutstandingText = () => {
    if (debtorClaim.transferredToExternalCollection) {
      return debtorClaim.externalDebtCollectionCompany!.name
        ? 'Overført til ' + debtorClaim.externalDebtCollectionCompany!.name
        : 'ekstern innkreving';
    }
    if (!debtorClaim.isTopLevelClaim || !debtorClaim.amount || debtorClaim.amount === 0) {
      return '';
    }
    return `Utestående: ${currencyFormatter.format(debtorClaim.amount!, { code: 'NOK' })}`;
  }

  const getDebtCollectionCaseType = () => {
    if (debtorClaim.isTopLevelClaim && debtorClaim.isParentCase) {
      return 'Hovedsak';
    }
    if (!debtorClaim.isTopLevelClaim && debtorClaim.type === 'LeapDebtCollectionClaim') {
      return 'Undersak';
    }
    return '';
  }

  return (
    <Badge
      color="primary"
      badgeContent={conversationActions}
      sx={{
        margin: '0 auto',
        maxWidth: '1000px',
        minWidth: '550px',
        ...(conversationActions === 0 ? { display: 'hidden' } : { display: 'flex' }),
      }}
    >
      <Card
        data-id="debtorClaimItem"
        sx={{
          margin: '0 auto 8px auto',
          width: '100%',
          maxWidth: '1000px',
          minWidth: '550px',
          paddingBottom: '0px',
          opacity: isSelectable ? enabledOpacity : disabledOpacity,
        }}
      >
        <CardContent
          sx={{
            display: 'flex',
            padding: 0,
            paddingBottom: 0,
            backgroundColor: bannerBackgroundColor,
          }}
        >
          <DebtorClaimIcon
            variant={debtorClaim.typeAndStatus}
            paymentType={debtorClaim.paymentType ?? PaymentType.Undefined}
            caseType={(debtorClaim as LeapClaim).caseType}
            isHalted={debtorClaim.isHalted}
          />

          <Box
            sx={{
              paddingLeft: '8px',
              paddingBottom: '8px',
              paddingTop: '8px',
              width: '100%',
              minWidth: '250px',
            }}
          >
            <React.Fragment>
              <DebtorClaimSystemBadge system={debtorClaim.type} />
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ExternalLinkToInvoice claimId={debtorClaim.externalReferenceId!} number={debtorClaim.number!} />

                {debtorClaim.paymentType === PaymentType.Invoice && !debtorClaim.transferredToExternalCollection && (
                    <ClaimPaymentInfoCopyButton debtorClaim={debtorClaim} />
                )}

                <ClaimInformationIcon claimInformationTooltip={debtorClaim.claimInformationTooltip!} />

                <Typography sx={{ fontWeight: 'bold', color: 'red', paddingLeft: '12px' }}>
                  {getDebtCollectionCaseType()}
                </Typography>
              </Box>
            </React.Fragment>
            <Box sx={{ paddingTop: '15px' }}>
              <CreditorName creditorName={debtorClaim.creditorName} />
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              paddingLeft: '8px',
              paddingBottom: '8px',
              paddingTop: '8px',
              width: '100%',
              maxWidth: '139px'
            }}
          >
            <Typography variant='body2'>
              Ordredato: {formatDate(debtorClaim.date)}
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              paddingLeft: '8px',
              paddingBottom: '8px',
              paddingTop: '8px',
              width: '100%',
              maxWidth: '128px'
            }}
          >
            <Typography sx={{ overflow: 'hidden', fontWeight: 'bold' }}>
              {getOutstandingText()}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex' }}>
            <IconButton
              onClick={handleExpandClick}
              aria-expanded={isExpanded}
              data-id="debtorClaimItemExpandButton"
              sx={{
                transform: 'rotate(0deg)',
                transition: (theme) => theme.transitions.create('transform', {
                  duration: theme.transitions.duration.shortest,
                }),
                marginLeft: 'auto',
                alignSelf: 'center',
                ...(isExpanded === true && {
                  transform: 'rotate(180deg)',
                }),
              }}
            >
              <ExpandMoreIcon />
            </IconButton>
          </Box>

          {showCheckBox
            ? (
              <Box sx={{ display: 'flex' }}>
                <Checkbox
                  sx={{ alignSelf: 'center', marginRight: '8px' }}
                  color="default"
                  checked={isSelected}
                  onChange={handleCheckboxChange}
                  disabled={!isSelectable}
                />
              </Box>
            )
            : <Box sx={{ display: 'flex', minWidth: '49px' }} />
          }
        </CardContent>

        <CardActions style={{ display: 'none' }} />

        <Collapse in={isExpanded} timeout="auto" unmountOnExit={true} style={{ backgroundColor: collapsePanelBackgroundColor }}>
          {!debtorClaim.transferredToExternalCollection && debtorClaim.kid && debtorClaim.kid.length > 0 && (
            <DebtorPaymentInformation
              kid={debtorClaim.kid}
              accountNumber={debtorClaim.debtorPaymentAccount}
              transactionsHaveBeenFetched={debtorClaim.transactionsHaveBeenFetched}
              transactions={debtorClaim.transactionsOnKid}
            />
          )}
          {debtorClaim.transferredToExternalCollection && (
            <ExternalDebtCollectionInfo companyInfo={debtorClaim.externalDebtCollectionCompany!} />
          )}

          <Box sx={{ maxWidth: '90%', margin: '30px auto', marginBottom: '50px' }}>
            {debtorClaim.eventsHaveBeenFetched
              ? debtorClaim.events && debtorClaim.events
                .sort((a, b) => {
                  const dateA = a.eventDate ? +a.eventDate : -Infinity;
                  const dateB = b.eventDate ? +b.eventDate : -Infinity;
                  return dateB - dateA;
                })
                .map((item, i) => (
                  <DebtorClaimEventComponent key={i} event={item} claimSystem={'leap'} debtorId={debtorClaim.debtorId!} />
                ))
              : <LinearProgress />
            }
          </Box>

          <DebtorClaimChildren childClaims={debtorClaim.childClaims} />
        </Collapse>
      </Card>
    </Badge>
  );
};

const mapStateToProps = (state: ApplicationState, ownProps: ComponentProps): PropsFromState => ({
  showCheckBox: ownProps.debtorClaim.number != null && ownProps.debtorClaim.number.length > 0,
  isSelected: state.debtorClaims.selectedDebtorClaims.find(x => x.number === ownProps.debtorClaim.number! && x.externalReferenceId === ownProps.debtorClaim.externalReferenceId!) !== undefined,
  conversationActions: getConversationActions(state, ownProps.debtorClaim),
  isSelectable: (isDebtorClaimSelectableForConversationSupervisorStep(state.conversationSupervisor, ownProps.debtorClaim) ?? false),
});

export default compose(
  connect(mapStateToProps)
)(DebtorClaimComponent);
